import React, { useState, useRef, useMemo, useEffect } from "react"
import { sliceFontSize, wrapSliceText, TEXT_MAX_RADIUS, TEXT_MIN_RADIUS } from "./SliceText"

const WHEEL = {
  centerX: 200,
  centerY: 200,
  radius: 186,
  centerCircleRadius: 27,
  textMinRadius: TEXT_MIN_RADIUS,
  textMaxRadius: TEXT_MAX_RADIUS,
  startAngleOffset: -90,
}

const ANIMATION = {
  durationMs: 5000,
  minRotations: 5,
  maxAdditionalRotations: 5,
  easing: "cubic-bezier(0.17, 0.67, 0.12, 0.99)",
}

const RIM_RADIUS = WHEEL.radius + 6
const POINTER_TIP_X = 375
const POINTER_BASE_X = 404
const POINTER_BASE_HALF = 14
const POINTER_HUB_RADIUS = 10
const COLORS = { dark: "#2D3436", light: "#fff", metal: "#3d4648", metalShadow: "#1a1e1f" }
const FONT = "Poppins, sans-serif"
const DEG_TO_RAD = Math.PI / 180

export const DEFAULT_SLOT_COUNT = 6
export const PLACEHOLDER_SLOTS = Array.from({ length: DEFAULT_SLOT_COUNT }, () => "Choice")

export const SLICE_COLORS = [
  "#FF6B6B",
  "#FFE66D",
  "#4ECDC4",
  "#A29BFE",
  "#FF6B9D",
  "#74b9ff",
  "#fd79a8",
  "#00b894",
  "#e17055",
  "#6c5ce7",
]

export const getSliceColor = (index, customColors = {}) =>
  customColors[index] || SLICE_COLORS[index % SLICE_COLORS.length]

export const toSlices = (texts, customColors = {}) =>
  texts.map((text, index) => ({ text, color: getSliceColor(index, customColors) }))

const CENTER_TEXT = [
  { text: "Tap", yOffset: -10, fontSize: 10 },
  { text: "to", yOffset: 0, fontSize: 8 },
  { text: "Spin", yOffset: 10, fontSize: 10 },
]

const TEXT_LINE_HEIGHT = 1.15

const baseTextStyle = {
  fontWeight: "bold",
  fontFamily: FONT,
  dominantBaseline: "central",
}

const sliceTextStyle = { ...baseTextStyle, fill: COLORS.dark, textAnchor: "middle" }
const centerTextStyle = { ...baseTextStyle, fill: COLORS.light, textAnchor: "middle" }

export const degToRadians = (deg) => deg * DEG_TO_RAD

export const normRotationAngle = (deg) => ((deg % 360) + 360) % 360

export const polarToCartesian = (radius, angleDeg) => ({
  x: WHEEL.centerX + radius * Math.cos(degToRadians(angleDeg)),
  y: WHEEL.centerY + radius * Math.sin(degToRadians(angleDeg)),
})

const slicePath = (index, sliceAngle) => {
  const startAngleDeg = index * sliceAngle + WHEEL.startAngleOffset
  const { centerX, centerY, radius } = WHEEL

  // A 360° arc is degenerate in SVG (start === end), so use two semicircles
  if (sliceAngle >= 360) {
    const top = polarToCartesian(radius, startAngleDeg)
    const bottom = polarToCartesian(radius, startAngleDeg + 180)
    return {
      path: `M ${centerX} ${centerY} L ${top.x} ${top.y} A ${radius} ${radius} 0 0 1 ${bottom.x} ${bottom.y} A ${radius} ${radius} 0 0 1 ${top.x} ${top.y} Z`,
      midAngle: 0,
    }
  }

  const midAngle = startAngleDeg + sliceAngle / 2
  const start = polarToCartesian(radius, startAngleDeg)
  const end = polarToCartesian(radius, startAngleDeg + sliceAngle)
  const largeArc = sliceAngle > 180 ? 1 : 0

  return {
    path: `M ${centerX} ${centerY} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y} Z`,
    midAngle,
  }
}

const TEXT_MID_RADIUS = (WHEEL.textMinRadius + WHEEL.textMaxRadius) / 2
const sliceTextPosition = (midAngle) => polarToCartesian(TEXT_MID_RADIUS, midAngle)

const GLOW_CLIP_MARGIN = 4
const GLOW_CLIP_R = WHEEL.centerCircleRadius + GLOW_CLIP_MARGIN
const GLOW_CLIP_PATH = `M0,0 H430 V400 H0 Z M${WHEEL.centerX},${WHEEL.centerY} m-${GLOW_CLIP_R},0 a${GLOW_CLIP_R},${GLOW_CLIP_R} 0 1,0 ${GLOW_CLIP_R * 2},0 a${GLOW_CLIP_R},${GLOW_CLIP_R} 0 1,0 -${GLOW_CLIP_R * 2},0`

export const randomSliceIndex = (sliceCount) => Math.floor(Math.random() * sliceCount)

const staticRotationAngle = (sliceIndex, sliceAngle) => 90 - (sliceIndex + 0.5) * sliceAngle

const wheelRotationAngle = (currentAngle, winningSliceIndex, sliceAngle) => {
  const targetAngle = staticRotationAngle(winningSliceIndex, sliceAngle)
  const offset = targetAngle - normRotationAngle(currentAngle)
  const normalizedOffset = offset <= 0 ? offset + 360 : offset
  const { minRotations, maxAdditionalRotations } = ANIMATION
  const extraRotations = (minRotations + Math.floor(Math.random() * maxAdditionalRotations)) * 360
  return currentAngle + normalizedOffset + extraRotations
}

const SpinWheel = ({ slices, onSpinStart, onSpinComplete, winnerIndex, idleSpin = true }) => {
  const activeSlices = slices || []
  const sliceCount = activeSlices.length
  const sliceAngle = sliceCount > 0 ? 360 / sliceCount : 360

  const [rotation, setRotation] = useState(0)
  const [isSpinning, setIsSpinning] = useState(false)
  const [hasSpun, setHasSpun] = useState(false)
  const [glowIndex, setGlowIndex] = useState(null)
  const rotationRef = useRef(0)

  useEffect(() => {
    setGlowIndex(null)
  }, [sliceCount])

  useEffect(() => {
    if (winnerIndex != null && winnerIndex >= 0 && winnerIndex < sliceCount) {
      const targetRotation = staticRotationAngle(winnerIndex, sliceAngle)
      rotationRef.current = targetRotation
      setRotation(targetRotation)
      setIsSpinning(false)
      setHasSpun(true)
      setGlowIndex(winnerIndex)
    }
  }, [winnerIndex, sliceAngle, sliceCount])

  const fontSize = sliceFontSize(sliceCount)
  const sliceGeometry = useMemo(
    () => Array.from({ length: sliceCount }, (_, i) => slicePath(i, sliceAngle)),
    [sliceCount, sliceAngle]
  )

  const spin = () => {
    if (isSpinning || sliceCount === 0) return

    const winningSliceIndex = randomSliceIndex(sliceCount)
    const winningSlice = activeSlices[winningSliceIndex]
    const newRotationAngle = wheelRotationAngle(rotationRef.current, winningSliceIndex, sliceAngle)

    setHasSpun(true)
    setIsSpinning(true)
    setGlowIndex(null)
    onSpinStart?.()

    requestAnimationFrame(() => {
      rotationRef.current = newRotationAngle
      setRotation(newRotationAngle)
    })

    setTimeout(() => {
      setIsSpinning(false)
      setGlowIndex(winningSliceIndex)
      onSpinComplete?.(winningSlice)
    }, ANIMATION.durationMs)
  }

  const transform = `rotate(${rotation}deg)`
  const transformOrigin = `${WHEEL.centerX}px ${WHEEL.centerY}px`

  const wheelStyle = {
    transform,
    transformOrigin,
    transition: isSpinning ? `transform ${ANIMATION.durationMs}ms ${ANIMATION.easing}` : "none",
    cursor: isSpinning ? "default" : "pointer",
  }

  const glowStyle = { transform, transformOrigin, transition: "none" }

  return (
    <div className="spin-wheel-container">
      <svg width="100%" height="100%" viewBox="0 0 430 400">
        <defs>
          {/* Shared slice gradient overlay for convex/domed look */}
          <radialGradient id="slice-grad" cx="40%" cy="35%" r="70%">
            <stop offset="0%" stopColor={COLORS.light} stopOpacity="0.25" />
            <stop offset="100%" stopColor="#000" stopOpacity="0.18" />
          </radialGradient>
          {/* Rim gradient */}
          <radialGradient id="rim-grad" cx="50%" cy="50%" r="50%">
            <stop offset="85%" stopColor={COLORS.metal} />
            <stop offset="100%" stopColor={COLORS.metalShadow} />
          </radialGradient>
          {/* Center hub gradient */}
          <radialGradient id="hub-grad" cx="40%" cy="35%" r="60%">
            <stop offset="0%" stopColor="#4a5557" />
            <stop offset="100%" stopColor={COLORS.dark} />
          </radialGradient>
          {/* Pointer body gradient */}
          <linearGradient id="pointer-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={COLORS.metal} />
            <stop offset="100%" stopColor={COLORS.metalShadow} />
          </linearGradient>
          {/* Pointer hub gradient */}
          <radialGradient id="pointer-hub-grad" cx="40%" cy="35%" r="60%">
            <stop offset="0%" stopColor={COLORS.metal} />
            <stop offset="100%" stopColor={COLORS.metalShadow} />
          </radialGradient>
          {/* Pointer drop shadow */}
          <filter id="pointer-shadow" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="1" dy="2" stdDeviation="2.5" floodOpacity="0.45" />
          </filter>
          {/* Winning slice edge glow */}
          <filter id="winner-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          {/* Clip glow to exclude center hub */}
          <clipPath id="glow-clip">
            <path d={GLOW_CLIP_PATH} clipRule="evenodd" />
          </clipPath>
        </defs>

        <g className={idleSpin && !hasSpun ? "wheel-idle" : undefined} style={wheelStyle} onClick={spin}>
          {/* Outer rim ring */}
          <circle
            cx={WHEEL.centerX}
            cy={WHEEL.centerY}
            r={RIM_RADIUS}
            fill="url(#rim-grad)"
            stroke={COLORS.metalShadow}
            strokeWidth="1.5"
          />

          {activeSlices.map((slice, i) => {
            const { path, midAngle } = sliceGeometry[i]
            const { lines } = wrapSliceText(slice.text, fontSize, sliceCount)
            const { x, y } = sliceTextPosition(midAngle)
            const lineHeight = fontSize * TEXT_LINE_HEIGHT
            const isMultiLine = lines.length > 1

            return (
              <g key={`slice-${i}`}>
                <path d={path} fill={slice.color} stroke="rgba(0,0,0,0.25)" strokeWidth="2" />
                <path d={path} fill="url(#slice-grad)" stroke="none" />
                <path d={path} fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.75" />
                <text x={x} y={y} {...sliceTextStyle} fontSize={fontSize} transform={`rotate(${midAngle}, ${x}, ${y})`}>
                  <title>{slice.text}</title>
                  {lines.map((line, lineIndex) => {
                    const dy = lineIndex === 0 ? (isMultiLine ? -lineHeight / 2 : 0) : lineHeight
                    return (
                      <tspan key={lineIndex} x={x} dy={dy}>
                        {line}
                      </tspan>
                    )
                  })}
                </text>
              </g>
            )
          })}

          {/* Center hub with gradient and highlight ring */}
          <circle
            cx={WHEEL.centerX}
            cy={WHEEL.centerY}
            r={WHEEL.centerCircleRadius + 2}
            fill="none"
            stroke="rgba(255,255,255,0.15)"
            strokeWidth="1.5"
          />
          <circle cx={WHEEL.centerX} cy={WHEEL.centerY} r={WHEEL.centerCircleRadius} fill="url(#hub-grad)" />

          {CENTER_TEXT.map(({ text, yOffset, fontSize }) => (
            <text key={text} x={WHEEL.centerX} y={WHEEL.centerY + yOffset} {...centerTextStyle} fontSize={fontSize}>
              {text}
            </text>
          ))}
        </g>

        {/* Winning slice edge glow (outside wheel group so it bleeds past the rim) */}
        {glowIndex != null && sliceGeometry[glowIndex] && (
          <g clipPath="url(#glow-clip)">
            <g style={glowStyle}>
              <path
                className="winner-glow"
                d={sliceGeometry[glowIndex].path}
                fill="none"
                stroke={activeSlices[glowIndex].color}
                strokeWidth="4"
                filter="url(#winner-glow)"
              />
            </g>
          </g>
        )}

        {/* Pointer: sculpted arrow with circular hub */}
        <g filter="url(#pointer-shadow)">
          {/* Main pointer body */}
          <polygon
            points={`${POINTER_TIP_X},${WHEEL.centerY} ${POINTER_BASE_X},${WHEEL.centerY - POINTER_BASE_HALF} ${POINTER_BASE_X},${WHEEL.centerY + POINTER_BASE_HALF}`}
            fill="url(#pointer-grad)"
            stroke={COLORS.metalShadow}
            strokeWidth="0.5"
          />
          {/* Top-edge highlight for bevel */}
          <line
            x1={POINTER_TIP_X + 1}
            y1={WHEEL.centerY}
            x2={POINTER_BASE_X}
            y2={WHEEL.centerY - POINTER_BASE_HALF + 1.5}
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="0.5"
          />
          {/* Circular hub at base */}
          <circle
            cx={POINTER_BASE_X}
            cy={WHEEL.centerY}
            r={POINTER_HUB_RADIUS}
            fill="url(#pointer-hub-grad)"
            stroke={COLORS.metalShadow}
            strokeWidth="0.75"
          />
          {/* Hub highlight ring */}
          <circle
            cx={POINTER_BASE_X}
            cy={WHEEL.centerY}
            r={POINTER_HUB_RADIUS - 2.5}
            fill="none"
            stroke="rgba(255,255,255,0.05)"
            strokeWidth="0.5"
          />
        </g>
      </svg>
    </div>
  )
}

export default SpinWheel
